import { useEffect, useRef, useState } from 'react';
import { InviteEmail } from './InviteEmail';
import { PairQR } from './PairQR';
import { markInviteSent } from '../lib/inviteSent';
import './InviteCard.css';

export interface InviteCardProps {
  /** The pair code the whole invite hangs off. null/'' → renders nothing. */
  code: string | null;
  /** /api/auth/config -> verifyEmail || forgotPassword. False hides the form. */
  mailEnabled: boolean;
  /** Prefix for the QR block's ids — chat-share-qr / pair-screen-qr. */
  idPrefix: string;
  /** 'pair' = Pair screen hero (waiting line + couple handle);
   *  'chat' = the share bar at the top of the empty chat. */
  variant?: 'pair' | 'chat';
  /** The couple's @handle, shown once the server has issued one. */
  inviteHandle?: string | null;
  /**
   * Card j1pvawhlLBks — when this invite stops working (epoch ms). The pair
   * code dies INVITE_TTL_MS (24 hours) after create; before this the card
   * kept showing a code that no longer paired, with no way to get another one.
   * null = unknown (never fetched), which renders no line at all.
   */
  expiresAt?: number | null;
  /** Re-issues the window. Present only on surfaces that can call the route
   *  (the pair screen, where the unpaired couple waits). */
  onRefresh?: () => Promise<{ ok: boolean; expiresAt?: number; message?: string }>;
  /** Fresh expiry after a successful re-issue. */
  onRefreshed?: (expiresAt: number) => void;
}

/** The link a partner opens: same shape PairQR encodes, so Copy, Share and
 *  the QR can never disagree about where the invite lands. */
export function inviteUrlFor(code: string): string {
  return `${window.location.origin}/?pair_code=${encodeURIComponent(code)}`;
}

/**
 * Card j1pvawhlLBks — the invite's remaining window, and the one action that
 * matters once it is gone.
 *
 * The pair code dies 24 hours after create, so "copy this code" is only
 * half an invite: with no clock the creator cannot tell a live code from a dead
 * one, and a short window means most codes are dead by the time the message
 * is read. Re-issuing extends the SAME code, so whatever the partner
 * already has in their hand starts working again — which is why this button,
 * not a new code, is the fix.
 */
function InviteExpiry({
  expiresAt,
  onRefresh,
  onRefreshed,
  idPrefix,
}: {
  expiresAt: number | null;
  /** Absent on surfaces that cannot call the route — the clock still shows,
   *  because knowing the code is dead is useful even where it cannot be
   *  revived. */
  onRefresh?: () => Promise<{ ok: boolean; expiresAt?: number; message?: string }>;
  onRefreshed?: (expiresAt: number) => void;
  idPrefix: string;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Half a minute is the resolution the sentence needs, and the tick is what
  // flips the card to "expired" on its own instead of waiting for a re-render.
  useEffect(() => {
    if (expiresAt == null) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (expiresAt == null) return null;
  const left = expiresAt - now;
  const expired = left <= 0;
  const mins = Math.max(1, Math.ceil(left / 60_000));
  // The window is 24 hours (INVITE_TTL_MS), so minutes alone would read "1440
  // minutes". Switch to hours once there is more than an hour and a half left,
  // and round DOWN so a fresh invite never claims more time than it has.
  const hrs = Math.floor(mins / 60);
  const span = hrs >= 2 ? `${hrs} hours` : `${mins} ${mins === 1 ? 'minute' : 'minutes'}`;

  const refresh = async () => {
    if (busy || !onRefresh) return;
    setBusy(true);
    setMessage(null);
    const r = await onRefresh();
    setBusy(false);
    if (r.ok && r.expiresAt) {
      setNow(Date.now());
      onRefreshed?.(r.expiresAt);
      setMessage('Fresh 24 hours — the same code works again.');
    } else {
      setMessage(r.message ?? 'Could not get a fresh code — try again.');
    }
  };

  return (
    <div className="pear-invite-expiry" data-testid="invite-expiry" data-expired={expired ? '1' : '0'}>
      {expired ? (
        <>
          <p className="pear-invite-expiry-line" role="status" id={`${idPrefix}-expired`}>
            This invite has run out.
            {onRefresh ? ' Get a fresh one — same code, so anything you already sent starts working again.' : ''}
          </p>
          {onRefresh && (
            <button
              type="button"
              className="pear-invite-btn"
              onClick={() => void refresh()}
              disabled={busy}
              data-test="invite-refresh"
            >
              {busy ? 'Getting a fresh code…' : 'Get a fresh code'}
            </button>
          )}
        </>
      ) : (
        <p className="pear-invite-expiry-line" role="status" id={`${idPrefix}-expiry`}>
          This invite works for {span} more.
        </p>
      )}
      {message && (
        <p className="pear-invite-card-error" role="alert" data-test="invite-refresh-note">
          {message}
        </p>
      )}
    </div>
  );
}

/**
 * The invite card (cards AnqHIFLf6a5y + the Pair hero block) — one component
 * for both surfaces that hold a pending code, so Copy / Share / Email / QR /
 * handle stay in lockstep.
 *
 * Copy writes the invite URL; Share hands the same URL to the platform sheet.
 * Three honesty rules the probe pins down:
 *   - `document.execCommand('copy') === false` is a FAILURE, not a copy:
 *     the button never says "Copied!" for a write that did not happen.
 *   - a cancelled share sheet (AbortError) writes nothing — the user just
 *     said no, so silently copying behind their back is wrong.
 *   - any other share failure says so and points back at Copy.
 */
export function InviteCard({
  code,
  mailEnabled,
  idPrefix,
  variant = 'chat',
  inviteHandle = null,
  expiresAt = null,
  onRefresh,
  onRefreshed,
}: InviteCardProps) {
  const [copied, setCopied] = useState(false);
  const [copyErr, setCopyErr] = useState<string | null>(null);
  const [shareErr, setShareErr] = useState<string | null>(null);

  /** Every copy attempt takes a ticket; a completion only speaks for the
   *  newest one, so a slow write from an earlier tap can never confirm — or
   *  clear — the feedback for a later invite (sol review 2026-10-13). */
  const attempt = useRef(0);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A new code is a new invite: the old confirmation belongs to the old one.
  useEffect(() => {
    attempt.current += 1;
    setCopied(false);
    setCopyErr(null);
    setShareErr(null);
  }, [code]);

  useEffect(() => () => {
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
  }, []);

  if (!code) return null;

  const pair = variant === 'pair';
  const url = inviteUrlFor(code);

  /** Legacy path: select a hidden textarea and ask the browser to copy.
   *  Returns what execCommand answered — false means it did NOT copy. */
  const legacyCopy = (text: string): boolean => {
    if (typeof document.execCommand !== 'function') return false;
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy') !== false;
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  };

  const copyUrl = async () => {
    const mine = ++attempt.current;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopied(false); // a retry starts from zero — never a stale "Copied!"
    setCopyErr(null);
    setShareErr(null);
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // Older browsers / non-HTTPS: fall back, but believe the answer.
      ok = legacyCopy(url);
    }
    if (mine !== attempt.current) return; // superseded — stay silent
    if (!ok) {
      setCopyErr('Copy did not work — select the code and copy it by hand.');
      return;
    }
    setCopied(true);
    // The link is on the clipboard — this device has invited (card
    // E5sHUI8IkN6b), so the first-act card stops offering the invite.
    markInviteSent();
    copiedTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  const shareUrl = async () => {
    setCopyErr(null);
    setShareErr(null);
    if (typeof navigator.share !== 'function') {
      // No share sheet here — copying the URL is the closest honest equivalent.
      await copyUrl();
      return;
    }
    try {
      await navigator.share({ title: 'Pear', text: `Join me on Pear — ${url}`, url });
      // The sheet accepted it — an invite has left this device.
      markInviteSent();
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return; // cancelled: no clipboard write behind the user's back
      }
      setShareErr('Sharing did not work — copy the link instead.');
    }
  };

  return (
    <div
      className={`pear-invite-card ${pair ? 'pear-invite-block' : 'pear-share-code'}`}
      aria-label="Pair code"
      data-test="invite-card"
    >
      <span className={pair ? 'pear-invite-heading' : 'pear-share-code-label'}>
        {pair
          ? 'Send this code to your partner. When they enter it, your shared space opens.'
          : 'Pair code — send this to your partner'}
      </span>

      <div className="pear-hero-code-row">
        <code className={pair ? 'pear-hero-code' : 'pear-code'}>{code}</code>
        <button
          type="button"
          className={`pear-invite-btn${copied ? ' pear-invite-btn--copied' : ''}`}
          onClick={() => void copyUrl()}
          aria-label="Copy invite link"
          data-test="invite-copy"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          type="button"
          className="pear-invite-btn"
          onClick={() => void shareUrl()}
          aria-label="Share invite link"
          data-test="invite-share"
        >
          Share
        </button>
      </div>

      {copyErr && (
        <p className="pear-invite-card-error" id={`${idPrefix}-copy-error`} role="alert" data-test="invite-copy-error">
          {copyErr}
        </p>
      )}
      {shareErr && (
        <p className="pear-invite-card-error" id={`${idPrefix}-share-error`} role="alert" data-test="invite-share-error">
          {shareErr}
        </p>
      )}

      {/* Email the code (card AnqHIFLf6a5y) — the third way out of the invite,
          beside Copy and Share. Hidden entirely when mail is not wired. */}
      <InviteEmail code={code} mailEnabled={mailEnabled} />

      {/* Scan-to-join QR (Jonathan email 2026-10-12) — same link, camera path. */}
      <PairQR code={code} idPrefix={idPrefix} />

      {pair && (
        <div className="pear-waiting">
          Waiting<span className="pear-waiting-dots">...</span>
        </div>
      )}

      {/* Card j1pvawhlLBks — the invite lives 24 hours. Say so, and when it
          is gone offer the one action that fixes it: the same code, a new
          window. Without this the creator sat on a dead code with no way out
          (and the partner holding it got an unexplained silence). */}
      {pair && expiresAt != null && (
        <InviteExpiry
          expiresAt={expiresAt}
          onRefresh={onRefresh}
          onRefreshed={onRefreshed}
          idPrefix={idPrefix}
        />
      )}

      {inviteHandle && (
        <p className="pear-invite-handle" id="invite-handle">
          Your couple is <code className="pear-invite-handle-value">@{inviteHandle}</code>
        </p>
      )}
    </div>
  );
}
